import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "../context/AuthContext";
import {
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Pencil,
  Trash2,
  Users,
  Calendar,
  Building2,
  FilePlus,
  Search,
  Shield,
  X,
  CheckCircle,
  UserPlus,
  MoreVertical,
  Filter,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
} from "lucide-react";
import DataTable from "../components/ui/DataTable";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import AllClients from "../components/ui/AllClients";
import CustomFilter from "../components/ui/CustomFilter";
import Pagination from "../components/ui/Pagination";
import ClientFormModal from "../components/ClientFormModal";
import MuiSelect from "../components/MuiSelect";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { clientService } from "../services/clientService";
import { userService } from "../services/userService";
import s3UploadService from "../services/s3UploadService";
import toast from "react-hot-toast";
import { formatName, formatDate, formatDateTime } from "../utils/formatters";
import { storeClientId } from "../utils/tokenUtils";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { Select } from "@radix-ui/react-select";
import MenuItem from "@mui/material/MenuItem";
import { Stack } from "@mui/material";
import { CLIENT_DELETION_GRACE_DAYS } from "../config/clientUi.constants";

/** @returns {boolean} true if range is valid when both ends set */
const isDateRangeValid = (range) => {
  if (!range || typeof range !== "object") return true;
  const { from, to } = range;
  if (from && to && String(from) > String(to)) return false;
  return true;
};

/** Axios/fetch cancellation — do not toast or mutate list state. */
const isAbortError = (e) =>
  e?.code === "ERR_CANCELED" ||
  e?.name === "CanceledError" ||
  e?.name === "AbortError";

const ClientManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // All state hooks must be at the top
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortType, setSortType] = useState("desc");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  /** Totals for stat cards (same filters/search as list; independent of active tab). */
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    active: 0,
    inactive: 0,
  });
  const [tabCountsLoading, setTabCountsLoading] = useState(false);
  const [clientsMutatedSeq, setClientsMutatedSeq] = useState(0);
  const [currentTab, setCurrentTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [pageSize] = useState(10);

  // Modal states
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientModalLoading, setClientModalLoading] = useState(false);

  // Deactivate states
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [deactivatingClient, setDeactivatingClient] = useState(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Permanent delete states
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] =
    useState(false);
  const [permanentDeletingClient, setPermanentDeletingClient] = useState(null);
  const [permanentDeleteLoading, setPermanentDeleteLoading] = useState(false);

  // Activate states
  const [activatingClient, setActivatingClient] = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);

  // Filter states
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterUserOptions, setFilterUserOptions] = useState([]);

  /** Chief-admin tabs: "all" must send is_active: null so search includes inactive clients (see clientService). */
  const isActiveForApi = useMemo(() => {
    if (currentTab === "active") return true;
    if (currentTab === "inactive") return false;
    return null;
  }, [currentTab]);

  useEffect(() => {
    const t = setTimeout(
      () => 
        setDebouncedSearchQuery(searchQuery.trim()),
      500,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  /** Resets page to 1 only when search / sort / filters / tab change — not on refresh or modal actions. */
  const clientsListFilterKey = useMemo(
    () =>
      JSON.stringify({
        debouncedSearchQuery,
        sortBy,
        sortType,
        appliedFilters,
        currentTab,
        pageSize,
      }),
    [
      debouncedSearchQuery,
      sortBy,
      sortType,
      appliedFilters,
      currentTab,
      pageSize,
    ],
  );

  const currentPageRef = useRef(1);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const prevFilterKeyRef = useRef(null);
const clientsListAbortRef = useRef(null);
const tabCountsAbortRef = useRef(null);
const listLoadGenerationRef = useRef(0);
const tabCountsGenerationRef = useRef(0);

const bumpClientsMutated = useCallback(() => {
  setClientsMutatedSeq((n) => n + 1);
}, []);

const tabCountsFilterKey = useMemo(
  () =>
    JSON.stringify({
      debouncedSearchQuery,
      appliedFilters,
      clientsMutatedSeq,
    }),
  [debouncedSearchQuery, appliedFilters, clientsMutatedSeq],
);

const getListFilterParams = useCallback(() => {
  const filterParams = {};

  if (appliedFilters.createdBy?.length) {
    filterParams.created_by = appliedFilters.createdBy;
  }

  if (appliedFilters.updatedBy?.length) {
    filterParams.updated_by = appliedFilters.updatedBy;
  }

  if (appliedFilters.createdAt?.from) {
    filterParams.created_at_from = appliedFilters.createdAt.from;
  }

  if (appliedFilters.createdAt?.to) {
    filterParams.created_at_to = appliedFilters.createdAt.to;
  }

  if (appliedFilters.updatedAt?.from) {
    filterParams.updated_at_from = appliedFilters.updatedAt.from;
  }

  if (appliedFilters.updatedAt?.to) {
    filterParams.updated_at_to = appliedFilters.updatedAt.to;
  }

  if (appliedFilters.client_type?.length) {
    filterParams.filters = { client_type: appliedFilters.client_type };
  }

  return filterParams;
}, [appliedFilters]);  const filterConfig = useMemo(
    () => ({
      sections: [
        {
          key: "createdBy",
          label: "Created By User",
          type: "checkbox",
          options: filterUserOptions,
        },
        {
          key: "updatedBy",
          label: "Updated By User",
          type: "checkbox",
          options: filterUserOptions,
        },
        {
          key: "client_type",
          label: "Client Type",
          type: "checkbox",
          options: [
            { label: "Test", value: "TEST" },
            { label: "Production", value: "Production" },
          ],
        },
        {
          key: "createdAt",
          label: "Created at",
          type: "dateRange",
        },
        {
          key: "updatedAt",
          label: "Updated at",
          type: "dateRange",
        },
      ],
    }),
    [filterUserOptions],
  );

  useEffect(() => {
    const role = user?.role_name || user?.role;
    if (!role) return;
    const normalizedRole = role.toLowerCase().replace(/[_\s-]/g, "-");
    if (normalizedRole !== "chief-admin") return;

    let cancelled = false;
    (async () => {
      try {
        /** Chief-admin list only; do not pass client_id (chief admins have no client). */
        const params =
  user?.role === 'chief-admin'
    ? { role: 'chief-admin', is_active: true }
    : user?.client_id
    ? {
        client_id: user.client_id,
        is_active: true,
        is_deleted: false,
      }
    : { is_active: true, is_deleted: false };

const res = await userService.findUsers(params, 1, 500);
        const users = res?.data?.users ?? res?.users ?? [];
        if (cancelled) return;

        const byId = new Map();
        for (const u of Array.isArray(users) ? users : []) {
          if (!u?.user_id) continue;
          if (!byId.has(u.user_id)) byId.set(u.user_id, u);
        }

        const formatLabel = (u) => {
          const name = (u.full_name || '').trim();
          const email = (u.email || '').trim();
          if (name && email) return `${name} (${email})`;
          if (email) return email;
          return name || u.user_id;
        };

        const options = Array.from(byId.values())
          .map((u) => ({
            label: formatLabel(u),
            value: u.user_id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

        setFilterUserOptions(options);
      } catch (e) {
        console.error("Failed to load users for client filters:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role_name, user?.role]);

  /** Hydrate filters from URL (?client_type=production&created_by=…&caf=&cat=&uaf=&uat=) */
  const filtersFromSearchParams = useMemo(() => {
    const out = {};
    const ct = searchParams.get("client_type");
    if (ct) {
      const parts = ct
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length) out.client_type = parts;
    }
    const cb = searchParams.get("created_by");
    if (cb) {
      const ids = cb
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length) out.createdBy = ids;
    }
    const ub = searchParams.get("updated_by");
    if (ub) {
      const ids = ub
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length) out.updatedBy = ids;
    }
    const caf = searchParams.get("caf") || "";
    const cat = searchParams.get("cat") || "";
    if (caf || cat) out.createdAt = { from: caf, to: cat };
    const uaf = searchParams.get("uaf") || "";
    const uat = searchParams.get("uat") || "";
    if (uaf || uat) out.updatedAt = { from: uaf, to: uat };
    return out;
  }, [searchParams]);

  const urlFiltersHydratedRef = useRef(false);
  useEffect(() => {
    if (urlFiltersHydratedRef.current) return;
    const role = (user?.role_name || user?.role || "")
      .toLowerCase()
      .replace(/[_\s-]/g, "-");
    if (role !== "chief-admin") return;
    if (Object.keys(filtersFromSearchParams).length === 0) {
      urlFiltersHydratedRef.current = true;
      return;
    }
    setAppliedFilters(filtersFromSearchParams);
    urlFiltersHydratedRef.current = true;
  }, [filtersFromSearchParams, user?.role_name, user?.role]);

  const loadClients = useCallback(
    async (pageArg) => {
      console.log("askdjhasjdbas")
      clientsListAbortRef.current?.abort();
      const ac = new AbortController();
      clientsListAbortRef.current = ac;
      const { signal } = ac;

      const gen = ++listLoadGenerationRef.current;
      const page =
        pageArg !== undefined && pageArg !== null
          ? pageArg
          : currentPageRef.current;
      try {
        setLoading(true);

        const filterParams = getListFilterParams();

        const response = await clientService.getClients({
          page,
          limit: pageSize,
          is_active: isActiveForApi,
          search_type: "client_name",
          search_query: debouncedSearchQuery || "",
          sort_by: sortBy ?? "created_at",
          sort_order: sortType ?? "desc",
          ...filterParams,
          signal,
        });

        if (gen !== listLoadGenerationRef.current) return;

        const clientsData = response.data?.clients || [];
        const paginationData = response.data?.pagination || {};

        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else {
          setClients([]);
        }

        const totalCount = paginationData.total || 0;
        const totalPagesCount = paginationData.totalPages || 1;

        setTotalClients(totalCount);
        setTotalPages(totalPagesCount);
        setCurrentPage(page);
      } catch (error) {
        if (isAbortError(error)) return;
        if (gen !== listLoadGenerationRef.current) return;
        console.error("Error loading clients:", error);
        toast.error("Failed to load clients. Please try again.");
        setClients([]);
        setTotalClients(0);
        setTotalPages(1);
      } finally {
        if (gen === listLoadGenerationRef.current) {
          setLoading(false);
        }
      }
    },
    [
      pageSize,
      currentTab,
      sortBy,
      sortType,
      debouncedSearchQuery,
      getListFilterParams,
      isActiveForApi,
    ],
  );

  const loadTabCounts = useCallback(async () => {
    const role = user?.role_name || user?.role;
    if (!role) return;
    const normalizedRole = role.toLowerCase().replace(/[_\s-]/g, "-");
    if (normalizedRole !== "chief-admin") return;

    tabCountsAbortRef.current?.abort();
    const ac = new AbortController();
    tabCountsAbortRef.current = ac;
    const { signal } = ac;

    const gen = ++tabCountsGenerationRef.current;
    const filterParams = getListFilterParams();
    const common = {
      page: 1,
      limit: 1,
      search_type: "client_name",
      search_query: debouncedSearchQuery || "",
      sort_by: sortBy ?? "created_at",
      sort_order: sortType ?? "desc",
      ...filterParams,
      signal,
    };

    try {
      setTabCountsLoading(true);
      const [allRes, activeRes, inactiveRes] = await Promise.all([
        clientService.getClients({ ...common, is_active: null }),
        clientService.getClients({ ...common, is_active: true }),
        clientService.getClients({ ...common, is_active: false }),
      ]);
      if (gen !== tabCountsGenerationRef.current) return;
      setTabCounts({
        all: Number(allRes?.data?.pagination?.total ?? 0),
        active: Number(activeRes?.data?.pagination?.total ?? 0),
        inactive: Number(inactiveRes?.data?.pagination?.total ?? 0),
      });
    } catch (e) {
      if (isAbortError(e)) return;
      if (gen !== tabCountsGenerationRef.current) return;
      console.error("Error loading client tab counts:", e);
    } finally {
      if (gen === tabCountsGenerationRef.current) {
        setTabCountsLoading(false);
      }
    }
  }, [
    user?.role_name,
    user?.role,
    debouncedSearchQuery,
    getListFilterParams,
    sortBy,
    sortType,
  ]);

  // Helper function to check if user is chief-admin (reusable)
  const isUserChiefAdmin = () => {
    const userRole = user?.role_name || user?.role;
    if (!userRole) return false;
    const normalizedRole = userRole.toLowerCase().replace(/[_\s-]/g, "-");
    return normalizedRole === "chief-admin";
  };

useEffect(() => {
  if (!isUserChiefAdmin()) return;

  const filterKeyChanged =
    prevFilterKeyRef.current !== clientsListFilterKey;

  const pageToLoad = filterKeyChanged ? 1 : currentPage;

  if (filterKeyChanged) {
    prevFilterKeyRef.current = clientsListFilterKey;

    // Reset pagination if needed
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }

  // Always load with correct page
  loadClients(pageToLoad);
}, [
  loadClients,
  currentPage,
  clientsListFilterKey,
  user?.role_name,
  user?.role,
]);

  useEffect(() => {
    if (!isUserChiefAdmin()) return;
    loadTabCounts();
  }, [tabCountsFilterKey, loadTabCounts, user?.role_name, user?.role]);

  useEffect(() => {
    return () => {
      clientsListAbortRef.current?.abort();
      tabCountsAbortRef.current?.abort();
    };
  }, []);

  const handleEditClient = (client) => {
    // Do not store client context — user stays in chief-admin scope; menu bar unchanged.
    navigate(
      `/settings?client_id=${client.client_id}&section=details&from=all-clients`,
    );
  };

  const handleDeactivateClient = (client) => {
    setDeactivatingClient(client);
    setShowDeactivateDialog(true);
  };

  const handlePermanentDeleteClient = (client) => {
    setPermanentDeletingClient(client);
    setShowPermanentDeleteDialog(true);
  };

  const handleActivateClient = async (client) => {
    try {
      setActivatingClient(client);
      setActivateLoading(true);
      await clientService.activateClient(client.client_id);
      await loadClients(); // Refresh the list
      bumpClientsMutated();
      toast.success("Client activated successfully");
    } catch (error) {
      console.error("Error activating client:", error);
      toast.error("Failed to activate client. Please try again.");
    } finally {
      setActivateLoading(false);
      setActivatingClient(null);
    }
  };

  const handleViewClientProjects = (client) => {
    storeClientId(client.client_id);
    const q = new URLSearchParams({
      client_id: client.client_id,
      client_name: client.client_name ?? "",
      from: "all-clients",
    });
    navigate(`/projects?${q.toString()}`);
  };

  const handleViewClientConfiguration = (client) => {
    // Do not switch global client context; Sidebar respects ?from=all-clients (see Sidebar.jsx).
    const q = new URLSearchParams({
      client_id: client.client_id,
      client_name: client.client_name ?? "",
      from: "all-clients",
    });
    navigate(`/client-configuration?${q.toString()}`);
  };

  const handleClientSubmit = async (clientData) => {
    try {
      setClientModalLoading(true);
      const { logoFile, logoToRemove, ...rest } = clientData;

      if (editingClient) {
        // Update existing client
        let logoUrl = editingClient.logo_url;
        if (logoFile) {
          const uploadedUrl = await s3UploadService.uploadFile(
            logoFile,
            "client-logos",
            editingClient.client_id,
          );
          logoUrl = uploadedUrl;
        } else if (logoToRemove) {
          logoUrl = null;
        }
        await clientService.updateClient(editingClient.client_id, {
          ...rest,
          logo_url: logoUrl,
        });
      } else {
        // Create new client first
        const createResponse = await clientService.createClient(rest);
        const createdClient = createResponse?.data || createResponse;
        const clientId = createdClient?.client_id;
        if (logoFile && clientId) {
          const uploadedUrl = await s3UploadService.uploadFile(
            logoFile,
            "client-logos",
            clientId,
          );
          await clientService.updateClient(clientId, {
            client_name: rest.client_name,
            client_description: rest.client_description || null,
            logo_url: uploadedUrl,
          });
        }
      }

      await loadClients(); // Refresh the list
      bumpClientsMutated();
      setShowClientModal(false);
    } catch (error) {
      console.error("Error saving client:", error);
      toast.error("Failed to save client. Please try again.");
    } finally {
      setClientModalLoading(false);
    }
  };

  const confirmDeactivateClient = async () => {
    if (!deactivatingClient) return;

    try {
      setDeactivateLoading(true);
      await clientService.deactivateClient(deactivatingClient.client_id);
      await loadClients(); // Refresh the list
      bumpClientsMutated();
      setShowDeactivateDialog(false);
      setDeactivatingClient(null);
      toast.success("Client deactivated successfully");
    } catch (error) {
      console.error("Error deactivating client:", error);
      toast.error("Failed to deactivate client. Please try again.");
    } finally {
      setDeactivateLoading(false);
    }
  };

  const confirmPermanentDeleteClient = async () => {
    if (!permanentDeletingClient) return;

    try {
      setPermanentDeleteLoading(true);
      await clientService.permanentDeleteClient(
        permanentDeletingClient.client_id,
      );
      await loadClients(); // Refresh the list
      bumpClientsMutated();
      setShowPermanentDeleteDialog(false);
      setPermanentDeletingClient(null);
      toast.success(
        "Client and all related data permanently deleted successfully",
      );
    } catch (error) {
      console.error("Error permanently deleting client:", error);
      toast.error("Failed to permanently delete client. Please try again.");
    } finally {
      setPermanentDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Active: "bg-success-light text-success",
      Inactive: "bg-error-light text-danger",
      Pending: "bg-warning-light text-warning",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || statusClasses.Inactive}`}
      >
        {status || "inactive"}
      </span>
    );
  };

  const columns = [
    {
      key: "client_name",
      header: "Client Name",
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden bg-primary-100 flex-shrink-0">
            {row.logo_url ? (
              <img
                src={row.logo_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Building2 className="w-4 h-4 text-primary-600" />
            )}
          </div>
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <div
                  className="font-medium text-text-primary cursor-pointer hover:text-primary-600 transition-colors flex items-center space-x-1"
                  onClick={() => handleViewClientProjects(row)}
                >
                  <span
                    style={{
                      color: "var(--color-primary-600)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "150px",
                    }}
                  >
                    {value}
                  </span>
                </div>
              </PopoverTrigger>
              <PopoverContent>
                <p className="text-sm">{value}</p>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ),
    },

    {
      key: "is_active",
      header: "Status",
      render: (value) => getStatusBadge(value ? "Active" : "Inactive"),
    },
    {
      key: "created_by",
      header: "Created By",
      render: (value) => formatName(value) || "-",
    },
    {
      key: "updated_by",
      header: "Updated By",
      render: (value) => formatName(value) || "-",
    },

    {
      key: "created_at",
      header: "Created At",
      sortable: true,
      render: (value) => (
        <div
          className="flex items-center space-x-2 text-sm"
          title={value ? formatDateTime(value) : undefined}
        >
          <Calendar className="w-3 h-3 text-text-tertiary" />
          <span>{formatDate(value)}</span>
        </div>
      ),
    },
    {
      key: "updated_at",
      header: "Updated At",
      sortable: true,
      render: (value) => (
        <div
          className="flex items-center space-x-2 text-sm"
          title={value ? formatDateTime(value) : undefined}
        >
          <Calendar className="w-3 h-3 text-text-tertiary" />
          <span>{formatDate(value)}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => (
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
                aria-label="Open actions menu"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleEditClient(row)}
                className="flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit client
              </DropdownMenuItem>
              {row.is_active ? (
                <DropdownMenuItem
                  onClick={() => handleDeactivateClient(row)}
                  className="flex items-center gap-2 text-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  Deactivate client
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => handleActivateClient(row)}
                  disabled={
                    activateLoading &&
                    activatingClient?.client_id === row.client_id
                  }
                  className="flex items-center gap-2 text-success"
                >
                  {activateLoading &&
                  activatingClient?.client_id === row.client_id ? (
                    <InlineLoadSkeleton className="h-4 w-4" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Activate client
                </DropdownMenuItem>
              )}
              {!row.is_deleted && (
                <DropdownMenuItem
                  onClick={() => handlePermanentDeleteClient(row)}
                  className="flex items-center gap-2 text-danger"
                >
                  <X className="w-4 h-4" />
                  Permanently delete
                </DropdownMenuItem>
              )}
              {isUserChiefAdmin() && (
                <DropdownMenuItem
                  onClick={() => handleViewClientConfiguration(row)}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Configure permissions
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const handleTabChange = (value) => {
    if (value === currentTab) return;
    setCurrentTab(value);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setSortBy(value);
  };

  const syncFiltersToUrl = useCallback(
    (filters) => {
      const next = new URLSearchParams(searchParams);
      const setOrDelete = (key, val) => {
        if (val != null && val !== "") next.set(key, val);
        else next.delete(key);
      };
      if (filters.client_type?.length)
        setOrDelete("client_type", filters.client_type.join(","));
      else next.delete("client_type");
      if (filters.createdBy?.length)
        setOrDelete("created_by", filters.createdBy.join(","));
      else next.delete("created_by");
      if (filters.updatedBy?.length)
        setOrDelete("updated_by", filters.updatedBy.join(","));
      else next.delete("updated_by");
      setOrDelete("caf", filters.createdAt?.from || "");
      setOrDelete("cat", filters.createdAt?.to || "");
      setOrDelete("uaf", filters.updatedAt?.from || "");
      setOrDelete("uat", filters.updatedAt?.to || "");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const validateClientListFilters = (filters) => {
    if (!isDateRangeValid(filters.createdAt)) {
      return 'Created at: "From" date must be before or equal to "To".';
    }
    if (!isDateRangeValid(filters.updatedAt)) {
      return 'Updated at: "From" date must be before or equal to "To".';
    }
    return null;
  };

  // Filter handlers — apply only on button (CustomFilter); validates date ranges before close
  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    syncFiltersToUrl(filters);
  };

  const handleResetFilters = () => {
    setAppliedFilters({});
    const next = new URLSearchParams(searchParams);
    [
      "client_type",
      "created_by",
      "updated_by",
      "caf",
      "cat",
      "uaf",
      "uat",
    ].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const hasActiveFilters = () => {
    return Object.keys(appliedFilters).some((key) => {
      const value = appliedFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "object" && value !== null) {
        return Object.values(value).some((v) => v);
      }
      return !!value;
    });
  };

  return (
    <div className="space-y-3">
      {/* Page Header */}
      <div
        className="flex justify-between items-start pl-2 pr-2"
        style={{
          marginTop: "12px",
          marginLeft: "8px",
          marginRight: "8px",
        }}
      >
        <div>
          <h1 className="text-2xl font-bold text-text-primary">All Clients</h1>
          <text
            style={{
              color: "#565D6DFF",
              fontSize: "18px",
              fontWeight: 400,
            }}
          >
            List of clients onboarded with us.{" "}
          </text>
        </div>
        {/* Search */}
        <div
          className=" "
          style={{
            display: "flex",
            justifyContent: "end",
            gap: "20px",
          }}
        >
          <div className="relative " style={{ height: "38px" }}>
            <Input
              id="search-clients"
              style={{ height: "38px", width: "265px" }}
              placeholder="Search by Client Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  loadClients();
                }
              }}
              className="pr-10"
            />
            <Button
              variant="ghost"
              size="icon"
              style={{
                background: "#9A9395FF",
              }}
              className="absolute right-0 top-0 h-full"
              onClick={() => loadClients()}
            >
              <Search
                className="w-4 h-4 text-primary-600"
                style={{ color: "#fff" }}
              />
            </Button>
          </div>
          <Stack flexDirection={"row"} gap={"2px"}>
            <MuiSelect
              label="Sort By"
              value={sortBy}
              onFilterChange={setSortBy}
              options={[
                { label: "Client Name", value: "client_name" },
                { label: "Updated", value: "updated_at" },
                { label: "Created", value: "created_at" },
              ]}
              sx={{ minWidth: "100px" }}
              className="bg-white"
            />
            <button
              onClick={() =>
                setSortType((prev) => (prev === "desc" ? "asc" : "desc"))
              }
              title={
                sortType === "desc"
                  ? "Descending — click to switch to Ascending"
                  : "Ascending — click to switch to Descending"
              }
              className="flex items-center justify-center w-10 h-[38px] border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              {sortType === "desc" ? (
                <ArrowDownWideNarrow className="w-5 h-5 text-gray-700" />
              ) : (
                <ArrowUpWideNarrow className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </Stack>

          {/* Filter Button */}
          <Button
            variant="outline"
            className="relative"
            style={{
              borderColor: hasActiveFilters() ? "#2E9E6F" : undefined,
              background: hasActiveFilters() ? "#f0fdf4" : undefined,
            }}
            onClick={() => setShowFilter(true)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters() && (
              <span className="ml-2 bg-[#2E9E6F] text-white text-xs px-2 py-0.5 rounded-full">
                {
                  Object.keys(appliedFilters).filter((key) => {
                    const value = appliedFilters[key];
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === "object")
                      return Object.values(value).some((v) => v);
                    return !!value;
                  }).length
                }
              </span>
            )}
          </Button>

          <Button
            type="button"
            className="bg-[var(--color-primary-600,#6D5EF3)] text-white hover:opacity-90 border-0"
            onClick={() => {
              navigate("/clients/create");
            }}
            variant="outline"
            disabled={loading}
          >
            <FilePlus className="w-4 h-4 mr-1 text-white" />
            Add New Client
          </Button>
        </div>
      </div>

      {/* Filter Drawer */}
      <CustomFilter
        config={filterConfig}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        initialFilters={appliedFilters}
        open={showFilter}
        onClose={() => setShowFilter(false)}
        validateBeforeApply={validateClientListFilters}
      />

      {/* Stats Cards */}
      <div
        className="flex justify-between pl-2 pr-2"
        style={{ marginLeft: "8px", marginRight: "8px" }}
      >
        <div
          className="card p-2 cursor-pointer transition-colors"
          style={{
            border:
              currentTab === "all" ? "1px solid var(--color-primary-600)" : "",
            width: "30%",
            background: "var(--color-bg-secondary)",
          }}
          onClick={() => handleTabChange("all")}
        >
          <div className="space-x-3" style={{ padding: "0px 16px" }}>
            <div style={{ display: "flex" }}>
              <div
                className="flex items-center"
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  marginRight: "15px",
                }}
              >
                <p
                  className="text-m font-medium"
                  style={{
                    color: "#655B5EFF",
                    fontSize: "18px",
                    fontWeight: 700,
                  }}
                >
                  Total Clients
                </p>
              </div>
              <div
                style={{
                  borderRadius: "50%",
                  width: "46px",
                  height: "40px",
                  background: "#EEEBECFF",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Building2
                  className="w-5 h-5 text-primary-600"
                  style={{ color: "#655B5EFF" }}
                />
              </div>
            </div>
            <div
              className="flex items-center gap-2 ml-1"
              style={{ minHeight: "44px" }}
            >
              <p
                className="text-m"
                style={{ fontWeight: 700, color: "#000", fontSize: "36px" }}
              >
                {tabCounts.all}
              </p>
              {tabCountsLoading ? (
                <InlineLoadSkeleton className="h-8 w-12 shrink-0" />
              ) : null}
            </div>
          </div>
        </div>
        <div
          className="card p-2 cursor-pointer transition-colors"
          style={{
            border:
              currentTab === "active"
                ? "1px solid var(--color-primary-600)"
                : "",
            width: "30%",
            background: "var(--color-bg-secondary)",
          }}
          onClick={() => handleTabChange("active")}
        >
          <div className="space-x-3" style={{ padding: "0px 16px" }}>
            <div style={{ display: "flex" }}>
              <div
                className="flex items-center"
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  marginRight: "15px",
                }}
              >
                <p
                  className="text-m font-medium"
                  style={{
                    color: "#197DCAFF",
                    fontSize: "18px",
                    fontWeight: 700,
                  }}
                >
                  Active Clients
                </p>
              </div>
              <div
                style={{
                  borderRadius: "50%",
                  width: "46px",
                  height: "40px",
                  background: "#ACD5F5FF",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Building2
                  className="w-5 h-5 text-primary-600"
                  style={{ color: "#655B5EFF" }}
                />
              </div>
            </div>
            <div
              className="flex items-center gap-2 ml-1"
              style={{ minHeight: "44px" }}
            >
              <p
                className="text-m"
                style={{ fontWeight: 700, color: "#000", fontSize: "36px" }}
              >
                {tabCounts.active}
              </p>
              {tabCountsLoading ? (
                <InlineLoadSkeleton className="h-8 w-12 shrink-0" />
              ) : null}
            </div>
          </div>
        </div>
        <div
          className="card p-2 cursor-pointer transition-colors"
          style={{
            border:
              currentTab === "inactive"
                ? "1px solid var(--color-primary-600)"
                : "",
            width: "30%",
            background: "var(--color-bg-secondary)",
          }}
          onClick={() => handleTabChange("inactive")}
        >
          <div className="space-x-3" style={{ padding: "0px 16px" }}>
            <div style={{ display: "flex" }}>
              <div
                className="flex items-center"
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  marginRight: "15px",
                }}
              >
                <p
                  className="text-m font-medium"
                  style={{
                    color: "#C12126FF",
                    fontSize: "18px",
                    fontWeight: 700,
                  }}
                >
                  Inactive Clients
                </p>
              </div>
              <div
                style={{
                  borderRadius: "50%",
                  width: "46px",
                  height: "40px",
                  background: "#F1ADAFFF",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Building2
                  className="w-5 h-5 text-primary-600"
                  style={{ color: "#655B5EFF" }}
                />
              </div>
            </div>
            <div
              className="flex items-center gap-2 ml-1"
              style={{ minHeight: "44px" }}
            >
              <p
                className="text-m"
                style={{ fontWeight: 700, color: "#000", fontSize: "36px" }}
              >
                {tabCounts.inactive}
              </p>
              {tabCountsLoading ? (
                <InlineLoadSkeleton className="h-8 w-12 shrink-0" />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Clients Table - margin so scrollbar doesn't overlap content when scrolling */}
      <div
        className="overflow-x-auto pl-2 pr-2"
        style={{ marginLeft: "8px", marginRight: "8px" }}
      >
        <AllClients
          clients={clients}
          handleDeactivateClient={handleDeactivateClient}
          handleActivateClient={handleActivateClient}
          handleEditClient={handleEditClient}
          handlePermanentDeleteClient={handlePermanentDeleteClient}
          handleViewClientConfiguration={handleViewClientConfiguration}
          handleViewClientProjects={handleViewClientProjects}
          activateLoading={activateLoading}
          activatingClientId={activatingClient?.client_id ?? null}
        />

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalClients}
          itemsPerPage={pageSize}
          onPageChange={loadClients}
        />
      </div>

      {/* Client Form Modal */}
      <ClientFormModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setEditingClient(null);
        }}
        onSubmit={handleClientSubmit}
        client={editingClient}
        loading={clientModalLoading}
      />

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeactivateDialog}
        onClose={() => {
          setShowDeactivateDialog(false);
          setDeactivatingClient(null);
        }}
        onConfirm={confirmDeactivateClient}
        title={`Make "${deactivatingClient?.client_name || "Client"}" Inactive?`}
        confirmText="Make Inactive"
        confirmVariant="danger"
        loading={deactivateLoading}
      >
        <div className="space-y-3 text-left">
          <p>This will move the client to the Inactive tab.</p>
          <p>
            You can restore the client within {CLIENT_DELETION_GRACE_DAYS} days.
            After that, the client will be permanently deleted.
          </p>
        </div>
      </ConfirmDialog>

      {/* Permanent Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPermanentDeleteDialog}
        onClose={() => {
          setShowPermanentDeleteDialog(false);
          setPermanentDeletingClient(null);
        }}
        onConfirm={confirmPermanentDeleteClient}
        title="Permanently Delete Client"
        message={`Are you sure you want to permanently delete "${permanentDeletingClient?.client_name}" and all its related data? This action cannot be undone. All projects, test cases, test runs, and other data associated with this client will be soft deleted and will not be visible in the UI.`}
        confirmText="Permanently Delete"
        confirmVariant="danger"
        loading={permanentDeleteLoading}
      />
    </div>
  );
};

export default ClientManagementPage;
